/**
 * Hook useReusedMain
 * 
 * Hook centralizado que maneja toda la lógica de negocio del módulo Reused/List.
 * Sigue el patrón establecido en Returned/List para consistencia en la arquitectura.
 * 
 * Responsabilidades:
 * - Manejo de filtros, búsqueda y sorting
 * - Orquestación de datos desde useReused
 * - Exposición de props para componentes de vista
 * - Detección de estados de filtros activos
 */

import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReused } from './useReused';

export const useReusedMain = (
    searchTerm?: string,
    externalFilterConditions?: FilterConditionOutput[],
    externalSorting?: { field: string; direction: 'asc' | 'desc' } | null
) => {
    const { t } = useTranslation('reusedModule');

    // Estados de filtros (usar externos si están disponibles)
    const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>([]);
    const [internalSearchTerm, setInternalSearchTerm] = useState('');
    const [internalSorting, setInternalSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);

    // Procesar filtros para enviar al hook
    const processedFilters = useMemo(() => {
        const currentFilterConditions = externalFilterConditions || filterConditions;
        const currentSorting = externalSorting || internalSorting;

        return {
            sortBy: currentSorting?.field ?? 'id',
            sortDirection: currentSorting?.direction ?? 'desc',
        };
    }, [externalFilterConditions, filterConditions, externalSorting, internalSorting]);

    // Memoizar el objeto de filtros para evitar re-renders infinitos
    const queryFilters = useMemo(() => ({
        searchTerm: searchTerm || internalSearchTerm,
        ...processedFilters,
    }), [searchTerm, internalSearchTerm, processedFilters]);

    // Hook de datos con scroll infinito
    const {
        reused,
        isLoading,
        isFetching,
        error,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        total,
    } = useReused(queryFilters);

    // Handlers de filtros
    const handleFilterChange = useCallback(
        (conditions: FilterConditionOutput[]) => {
            setFilterConditions(conditions);
        },
        []
    );

    const handleSearch = useCallback((term: string) => {
        setInternalSearchTerm(term);
    }, []);

    const handleSortingChange = useCallback((newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
        setInternalSorting(newSorting);
    }, []);

    // Props compartidas para ambas vistas (Mobile/Desktop)
    const reusedViewProps = useMemo(
        () => ({
            reused,
            isLoading,
            isFetching,
            fetchNextPage,
            hasNextPage,
            isFetchingNextPage,
            total,
            refetch,
            sorting: externalSorting || internalSorting,
            onSortingChange: handleSortingChange,
        }),
        [
            reused,
            isLoading,
            isFetching,
            fetchNextPage,
            hasNextPage,
            isFetchingNextPage,
            total,
            refetch,
            externalSorting,
            internalSorting,
            handleSortingChange,
        ]
    );

    // Detectar si hay filtros activos
    const hasActiveFilters = useMemo(() => {
        const currentFilterConditions = externalFilterConditions || filterConditions;
        const currentSearchTerm = searchTerm || internalSearchTerm;
        const currentSorting = externalSorting || internalSorting;

        return (
            currentFilterConditions.length > 0 ||
            currentSearchTerm !== '' ||
            currentSorting !== null
        );
    }, [externalFilterConditions, filterConditions.length, searchTerm, internalSearchTerm, externalSorting, internalSorting]);

    return {
        // Estados
        filterConditions: externalFilterConditions || filterConditions,
        searchTerm: searchTerm || internalSearchTerm,
        sorting: externalSorting || internalSorting,

        // Datos
        reused,
        isLoading,
        isFetching,
        error,
        total,

        // Props para componentes
        reusedViewProps,

        // Handlers
        handleFilterChange,
        handleSearch,
        handleSortingChange,
        refetch,

        // Props para infinite scroll
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,

        // Estado de filtros
        hasActiveFilters,
    };
};

